import { Button, Text } from '@inmotionnow/momentum-components-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'

import type { BulkImportRow } from './bulkImportApi'

export interface BulkImportRowListProps {
  rows: BulkImportRow[]
  /** Retry one row. Absent while the list is read-only. */
  onRetryRow?: (rowId: number) => void
  /** Rows whose retry is in flight, so each button shows as working on its own. */
  retryingRowIds?: ReadonlySet<number>
}

/**
 * The backend sends each leg's status as the raw enum value it stores — `provisioning`,
 * `ready`, `error`, `pending_invite`, `disabled`, `deleted`. Those are identifiers, not
 * copy, and showing one beside the row's own translated status put "Sign-in:
 * pending_invite" next to "Failed" on the same line. Anything unrecognised falls back to
 * the raw value rather than rendering a missing key, so a status added later degrades to
 * something truthful instead of to nothing.
 */
const integrationStatusLabel = (
  value: string | null,
  t: (key: string) => string,
): string => {
  if (value === null) return t('BULK_IMPORT_INTEGRATION_NONE')
  const key = `BULK_IMPORT_INTEGRATION_STATUS_${value.toUpperCase()}`
  const translated = t(key)
  return translated === key ? value : translated
}

/** Integrations in the order they are provisioned; Workflow's user needs DAM's id. */
const INTEGRATIONS: ReadonlyArray<
  [key: keyof BulkImportRow['integrations'], label: string]
> = [
  ['identity', 'BULK_IMPORT_INTEGRATION_IDENTITY'],
  ['dam', 'BULK_IMPORT_INTEGRATION_DAM'],
  ['workflow', 'BULK_IMPORT_INTEGRATION_WORKFLOW'],
]

/**
 * The rows of a run that still need something from the admin.
 *
 * The backend sends only those rows, in the order of the file they came from, and both
 * facts are load-bearing: a row that produced a working user needs no decision, and the
 * line number is what lets the admin find the row in their own spreadsheet. Neither the
 * selection nor the order is second-guessed here.
 *
 * Rows still moving on their own are listed alongside the failed ones, because seeing
 * what is outstanding is the point of this list — but they are not *counted* with them,
 * which is why the headline numbers keep failures and work-in-progress apart.
 *
 * Only a failed row offers a retry. A row that has spent its attempts says so and
 * offers nothing: a button that cannot do anything is worse than no button, because the
 * admin spends a click and a wait learning what the row could have told them. A row
 * still in flight offers nothing either — there is nothing yet to retry.
 */
export const BulkImportRowList = ({
  rows,
  onRetryRow,
  retryingRowIds,
}: BulkImportRowListProps): React.ReactElement => {
  const { t } = useTranslation()

  return (
    <ul data-testid='bulk-import-rows'>
      {rows.map(row => (
        <li key={row.id} data-testid='bulk-import-row'>
          <Text
            tag='span'
            textWeight='bold'
            dataTestingId='bulk-import-row-line'
          >
            {`${t('BULK_IMPORT_LINE')} ${row.source_line_number}`}
          </Text>{' '}
          <Text tag='span' dataTestingId='bulk-import-row-email'>
            {row.email}
          </Text>{' '}
          <Text tag='span' dataTestingId='bulk-import-row-status'>
            {t(`BULK_IMPORT_ROW_STATUS_${row.status.toUpperCase()}`)}
          </Text>
          {row.error_message && (
            <>
              {' '}
              <Text tag='span' dataTestingId='bulk-import-row-error'>
                {row.error_message}
              </Text>
            </>
          )}
          {row.retry_count > 0 && (
            <>
              {' '}
              <Text
                tag='span'
                size='sm'
                dataTestingId='bulk-import-row-attempts'
              >
                {`${t('BULK_IMPORT_ATTEMPTS')}: ${row.retry_count}`}
              </Text>
            </>
          )}
          {row.status === 'needs_support' && (
            <>
              {' '}
              <Text
                tag='span'
                size='sm'
                dataTestingId='bulk-import-row-exhausted'
              >
                {t('BULK_IMPORT_ROW_EXHAUSTED')}
              </Text>
            </>
          )}
          {onRetryRow && row.status === 'error' && (
            <>
              {' '}
              <Button
                label={t('BULK_IMPORT_RETRY_ROW')}
                theme='flat'
                size='sm'
                loading={retryingRowIds?.has(row.id) ?? false}
                dataTestingId={`bulk-import-retry-row-${row.id}`}
                // Every one of these reads "Retry" to a screen reader otherwise, with
                // nothing to say which of N rows it belongs to.
                ariaLabel={`${t('BULK_IMPORT_RETRY_ROW')} — ${t(
                  'BULK_IMPORT_LINE',
                )} ${row.source_line_number}, ${row.email}`}
                onClick={() => onRetryRow(row.id)}
              />
            </>
          )}
          <ul data-testid='bulk-import-row-integrations'>
            {INTEGRATIONS.map(([key, label]) => (
              <li key={key} data-testid={`bulk-import-integration-${key}`}>
                <Text tag='span' size='sm'>
                  {`${t(label)}: ${integrationStatusLabel(
                    row.integrations[key],
                    t,
                  )}`}
                </Text>
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  )
}
